import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import dayjs from "dayjs";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet } from "@shared/api/restApi";
import { apiFetch } from "@shared/api/api";
import { latestNoMessage } from "@shared/libs/pushStore";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnTab from "@shared/components/SnTab";
import SnButton from "@shared/components/SnButton";
import { menuInfo } from "@shared/type/CommonType";
import { SnGridRef, ColumnConfig } from "@shared/components/GridV2/SnGridTypes";
import { global } from "@shared/libs/Global";

interface Props {
  Menuprops: menuInfo;
}

/** "yyyy-MM-dd HH:mm:ss.SSS" → "yyyy-MM-dd HH:mm:ss" */
const fmtDateTime = (params: any) => {
  const v = params?.value;
  return typeof v === "string" && v.length > 19
    ? v.substring(0, 19)
    : (v ?? "");
};

const SN_ETC_ALR_REP_001 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
    grid3: React.createRef<SnGridRef>(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const { userInfo } = global(); // Login user 정보
  const searchRef = useRef<any>(null);
  const cd_menu = "SN_ETC_ALR_REP_001";
  //#endregion

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding

  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });

  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });

  const [selectedTab, setSelectedTab] = useState<string>("1");
  //#endregion

  //#region  로컬 메타 (getInitFormData 대체)
  /** 조회조건 컨트롤 정의 - 12칸 그리드를 4/4/4로 나눈다 */
  const buildFormControlData = () => {
    const base = {
      cd_firm: userInfo.cd_firm,
      cd_menu,
      cd_user: userInfo.cd_user,
      cd_form: "search",
      fg_visible: "Y",
      yn_required: "N",
      qt_rowindex: 1,
      qt_rowspan: 1,
    };
    return [
      {
        ...base,
        no_line: 1,
        no_seq: 1,
        tp_ctrl: "SnPeriodBox",
        nm_label: "기간",
        cd_param: "dt_from,dt_to",
        qt_colindex: 1,
        qt_colspan: 4,
      },
      {
        ...base,
        no_line: 2,
        no_seq: 2,
        tp_ctrl: "SnComboBox",
        nm_label: "모듈",
        cd_param: "cd_module",
        qt_colindex: 5,
        qt_colspan: 4,
        cd_clas: "SYS002",
      },
      {
        ...base,
        no_line: 3,
        no_seq: 3,
        tp_ctrl: "SnTextBox",
        nm_label: "검색",
        cd_param: "str_search",
        qt_colindex: 9,
        qt_colspan: 4,
      },
    ];
  };

  /** 그리드 컬럼 정의 */
  const initColumns = (cd_grid: string): ColumnConfig[] => {
    const col = (
      cd_column: string,
      nm_column: string,
      qt_width: number,
      no_seq: number,
      extra?: Partial<ColumnConfig>
    ): ColumnConfig => ({
      cd_firm: userInfo.cd_firm ?? "",
      cd_menu,
      cd_grid,
      cd_user: userInfo.cd_user ?? "",
      cd_column,
      nm_column,
      tp_column: "SnTextBox",
      fg_visible: "Y",
      no_seq,
      qt_width,
      yn_allowsorting: "Y",
      yn_sum: "N",
      ...extra,
    });
    return [
      col("no_message", "메시지번호", 0, 0, { fg_visible: "N" }), // 읽음처리 키(숨김)
      col("yn_read", "확인여부", 0, 0, { fg_visible: "N" }), // 전체 결과셋에만 값이 있음(숨김)
      col("tm_reg", "수신일시", 170, 1, { valueFormatter: fmtDateTime }),
      col("nm_menu", "메뉴명", 200, 2),
      col("dc_message", "메시지", 600, 3),
      col("no_ref", "관련번호", 180, 4),
      col("nm_user", "발생자", 100, 5),
      col("tm_read", "확인일시", 170, 6, { valueFormatter: fmtDateTime }),
    ];
  };
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        setFormControlData(buildFormControlData()); // 조회조건 항목 설정
        const initFormData = {
          dt_from: dayjs().format("YYYYMMDD"), // 기본: 오늘 ~ 오늘
          dt_to: dayjs().format("YYYYMMDD"),
          cd_module: "",
          str_search: "",
        };
        setFormData(initFormData);

        setColums({
          grid1: initColumns("grid1"),
          grid2: initColumns("grid2"),
          grid3: initColumns("grid3"),
        });

        // 화면 오픈 시 자동조회 - SearchPanel 바인딩을 기다리지 않도록 초기 조회조건을 직접 넘긴다
        await onSearch(initFormData);
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
  }));

  //#region onSearch
  const onSearch = async (searchData: any) => {
    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      const params = {
        id: "FcmAlarmMapper",
        queryId: "sn_etc_alr_rep_001_01",
        params: {
          ...searchDataToUse,
          cd_user: userInfo.cd_user, // 수신자(ar.cd_user_rcv) - cd_firm은 서버가 채운다
        },
      };

      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0] || [],
        grid2: data[1] || [],
        grid3: data[2] || [],
      });
    } catch (error: any) {}
  };
  //#endregion

  //#region onSnButtonClick
  const onSnButtonClick = async (id: string) => {
    switch (id) {
      case "ReadAll": {
        const unreadRows: any[] = dataSources.grid2 ?? [];
        if (unreadRows.length === 0) return;

        // 미확인 중 가장 최신(tm_reg) no_message를 보내면 그 시각 이전 건을 모두 읽음처리
        const latest = latestNoMessage(unreadRows);
        if (!latest) return;
        await apiFetch("/alarm/read-all", {
          method: "POST",
          body: { no_message: latest } as any,
        });
        await onSearch(null);
        break;
      }
    }
  };
  //#endregion

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
      },
    }),
    [columns, dataSources]
  );

  const tabTitles = useMemo(
    () => [
      `전체(${dataSources.grid1?.length ?? 0})`,
      `미확인(${dataSources.grid2?.length ?? 0})`,
      "확인",
    ],
    [dataSources]
  );
  //#endregion

  const snButtons = (
    <div>
      <SnButton
        text={"모두읽음처리"}
        onClick={() => {
          onSnButtonClick("ReadAll");
        }}
      />
    </div>
  );

  const renderGrid = (key: "grid1" | "grid2" | "grid3") => (
    <SnGrid
      ref={gridRefs.current[key]} // Grid Name
      columnConfig={grids[key].columns} // 컬럼 설정 전달
      rowData={grids[key].dataSource} // Datasource
      etcProps={{
        rowSelection: "single",
        isEditable: false,
        enableInitialSort: false, // 조회 직후 첫 컬럼 자동 정렬 해제 - 매퍼 정렬(tm_reg desc) 유지
        wrapText: true, // 긴 메시지가 잘리지 않게 셀 줄바꿈 + 행 높이 자동
      }}
    />
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnTab
          titles={tabTitles}
          selectedTab={selectedTab}
          onTabChanged={setSelectedTab}
          header={[
            <React.Fragment key="h1">{snButtons}</React.Fragment>,
            <React.Fragment key="h2">{snButtons}</React.Fragment>,
            <React.Fragment key="h3" />,
          ]}
          body={[
            <React.Fragment key="grid1">{renderGrid("grid1")}</React.Fragment>,
            <React.Fragment key="grid2">{renderGrid("grid2")}</React.Fragment>,
            <React.Fragment key="grid3">{renderGrid("grid3")}</React.Fragment>,
          ]}
        />
      </div>
    </div>
  );
});

export default SN_ETC_ALR_REP_001;
